/**
 * Agentic AI — Floating Chat Panel
 *
 * Right-side slide-out panel accessible from any module. Powered by Cognara
 * via /api/agents/run. Shows:
 *   - Conversation thread
 *   - Tool calls inline (collapsible)
 *   - HIGH_WRITE approvals as inline "Approve / Reject" cards
 *   - Persona switcher (Orchestrator / Sourcing / Onboarding)
 */
import { useState, useRef, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Bot, Send, X, Sparkles, Loader2, ChevronDown, ChevronRight,
  Check, X as XIcon, AlertTriangle, Wrench,
} from 'lucide-react';
import { toast } from 'sonner';

const tk = () => localStorage.getItem('clm_token') || localStorage.getItem('token');
const h = () => ({ headers: { Authorization: `Bearer ${tk()}` } });

const PERSONAS = [
  { id: 'orchestrator', label: 'Orchestrator', icon: '🧭' },
  { id: 'sourcing', label: 'Sourcing', icon: '🛒' },
  { id: 'onboarding', label: 'Onboarding', icon: '🤝' },
  { id: 'category', label: 'Category', icon: '📊' },
  { id: 'risk', label: 'Risk', icon: '⚠️' },
  { id: 'performance', label: 'Performance', icon: '📈' },
  { id: 'contract', label: 'Contract', icon: '📜' },
  { id: 'diversity', label: 'Diversity', icon: '🌍' },
];

// Per-persona guidance & sample prompts shown when the chat is empty.
// Helps users discover what Cognara can actually do in each context.
const PERSONA_GUIDE = {
  orchestrator: {
    blurb: 'Top-level coordinator — can route work to any specialist agent, summarise across modules, and trigger cross-functional flows.',
    cando: ['Cross-module summaries (sourcing → contract → claims)', 'Approval routing & escalation', 'KPI drill-downs by BU / category'],
    prompts: [
      'Give me a portfolio summary of all active contracts > $5M with rising risk',
      'Which open sourcing events are at risk of missing their bid-due deadline?',
      'Brief me on weekly category health across the top 5 spend BUs',
    ],
  },
  sourcing: {
    blurb: 'Sourcing specialist — covers SS-4.1 through SS-4.12 (qualification → contract initiation). Use for event creation, bid analysis, award recommendations.',
    cando: ['Draft RFQ/ITT/RFP scope', 'Score & rank bids', 'Generate award recommendations with DOA routing'],
    prompts: [
      'Draft an RFQ scope for steel rebar supply, 18-month framework',
      'Score the open BAFO event SEV-2026-014 on price + delivery + diversity',
      'Recommend award for SEV-2026-009 and route per DOA matrix',
    ],
  },
  onboarding: {
    blurb: 'Onboarding agent — runs supplier qualification, KYC, banking, references, financials and diversity checks end-to-end.',
    cando: ['Trigger qualification workflow', 'Re-run financial / risk score', 'Resend questionnaire to a supplier'],
    prompts: [
      'Onboard SUP-2026-118 for raw_materials_metals — start full qualification',
      'Re-run financial health on Acme Industries — flag if Altman-Z dropped',
      'Send a diversity questionnaire to all open supplier registrations',
    ],
  },
  category: {
    blurb: 'Category strategist — Kraljic positioning, spend cube, market intelligence, demand planning, taxonomy and strategy docs.',
    cando: ['Kraljic re-classification', 'Spend cube drill-downs', 'Generate or refresh a category strategy doc'],
    prompts: [
      'Refresh the Kraljic quadrant for "Bulk chemicals" — explain any movement',
      'Top 10 maverick-spend categories last quarter and why',
      'Draft a 12-month strategy for "MRO consumables"',
    ],
  },
  risk: {
    blurb: 'Risk monitor — supplier financial / operational / geopolitical / ESG risk. Force-Majeure trigger and obligation impact analysis.',
    cando: ['Score a supplier portfolio', 'Trigger FM declaration impact analysis', 'Surface contracts breaching risk thresholds'],
    prompts: [
      'Rank our top-50 suppliers by composite risk this quarter',
      'If FM is declared on CTR-001 (pandemic), which obligations are affected?',
      'Which active contracts have suppliers with Altman-Z < 1.8?',
    ],
  },
  performance: {
    blurb: 'Performance analyst — KPI scorecards, OTIF, defect rates, CAPAs, balanced-scorecard, supplier reviews.',
    cando: ['Pull a supplier scorecard', 'Compare OTIF across BUs', 'Auto-draft a CAPA from defect rate breach'],
    prompts: [
      'Show the latest scorecard for "Acme Industries" with delta vs last quarter',
      'Which suppliers slipped below 85 % OTIF in the last 90 days?',
      'Draft a CAPA for SUP-2026-118 (3 defect events) — moderate severity',
    ],
  },
  contract: {
    blurb: 'Contract steward — repository, clause analysis, obligation tracking, amendments (incl. Change Orders), milestones.',
    cando: ['Find clauses by keyword', 'Roll up amendment cost impact', 'List upcoming obligation deadlines'],
    prompts: [
      'Find all "limitation of liability" clauses across active services contracts',
      'Total cost impact of approved Change Orders on CTR-001 this fiscal',
      'Which obligations are due in the next 30 days and overdue ?',
    ],
  },
  diversity: {
    blurb: 'Diversity programme lead — MBE / WBE / Veteran / MSME tracking, tier-2 reporting, diversity-first nudges in sourcing.',
    cando: ['Diversity spend by tier', 'Add diverse-first nudge to a sourcing event', 'Track tier-2 supplier reporting compliance'],
    prompts: [
      'What is our YTD diverse-supplier spend by tier (MBE / WBE / Veteran)?',
      'Which open events qualify for a diversity-first nudge?',
      'List suppliers behind on tier-2 spend reporting',
    ],
  },
};

export default function AgentChatPanel({ open, onClose, initialPrompt = '', initialPersona = null }) {
  const [persona, setPersona] = useState('orchestrator');
  const [prompt, setPrompt] = useState(initialPrompt);
  const [history, setHistory] = useState([]);  // [{ kind, ...}]
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  // Persistent session id — enables multi-turn memory across messages
  const [sessionId] = useState(() => {
    let sid = sessionStorage.getItem('agent_session_id');
    if (!sid) {
      sid = `chat-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      sessionStorage.setItem('agent_session_id', sid);
    }
    return sid;
  });

  useEffect(() => {
    if (initialPrompt) setPrompt(initialPrompt);
  }, [initialPrompt]);

  useEffect(() => {
    if (initialPersona && PERSONAS.find(p => p.id === initialPersona)) {
      setPersona(initialPersona);
    }
  }, [initialPersona]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [history, busy]);

  const send = async () => {
    if (!prompt.trim() || busy) return;
    const userMsg = prompt.trim();
    setHistory(prev => [...prev, { kind: 'user', text: userMsg },
                                    { kind: 'streaming', text: 'Thinking…', tools: [] }]);
    setPrompt('');
    setBusy(true);
    // Get backend URL same way axios does
    const baseURL = api.defaults.baseURL || '';
    try {
      const resp = await fetch(`${baseURL}/agents/run-stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tk()}`,
        },
        body: JSON.stringify({ prompt: userMsg, persona, session_id: sessionId }),
      });
      if (!resp.ok || !resp.body) {
        throw new Error(`HTTP ${resp.status}`);
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      const toolsInProgress = [];  // [{ tool, args, result }]
      let finalAnswer = '';
      let approvalsList = [];
      let runId = '';

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        // SSE frames separated by \n\n
        const parts = buf.split('\n\n');
        buf = parts.pop() || '';
        for (const p of parts) {
          if (!p.startsWith('data: ')) continue;
          let evt;
          try { evt = JSON.parse(p.slice(6)); } catch { continue; }
          if (evt.kind === 'RUN_STARTED') {
            runId = evt.run_id;
          } else if (evt.kind === 'TOOL_CALL') {
            toolsInProgress.push({ tool: evt.tool, args: evt.args, result: null });
            setHistory(prev => {
              const next = [...prev];
              next[next.length - 1] = {
                kind: 'streaming',
                text: `Calling ${evt.tool}…`,
                tools: [...toolsInProgress],
              };
              return next;
            });
          } else if (evt.kind === 'TOOL_RESULT') {
            const last = toolsInProgress[toolsInProgress.length - 1];
            if (last) last.result = evt.result_preview;
            setHistory(prev => {
              const next = [...prev];
              next[next.length - 1] = {
                kind: 'streaming',
                text: `Got result from ${evt.tool}`,
                tools: [...toolsInProgress],
              };
              return next;
            });
          } else if (evt.kind === 'LLM_TEXT') {
            // Show interim text
            setHistory(prev => {
              const next = [...prev];
              next[next.length - 1] = {
                kind: 'streaming',
                text: evt.text,
                tools: [...toolsInProgress],
              };
              return next;
            });
            finalAnswer = evt.text;
          } else if (evt.kind === 'RUN_COMPLETED') {
            finalAnswer = evt.final_answer || finalAnswer;
            approvalsList = evt.approvals || [];
            runId = evt.run_id || runId;
          } else if (evt.kind === 'RUN_ERROR') {
            throw new Error(evt.error || 'Run error');
          }
        }
      }

      // Replace streaming placeholder with final structured items
      setHistory(prev => {
        const next = prev.slice(0, -1);  // drop streaming bubble
        const toolItems = toolsInProgress.map(t => ({
          kind: 'tool', tool: t.tool, args: t.args, result: t.result,
        }));
        return [
          ...next,
          ...toolItems,
          { kind: 'assistant', text: finalAnswer || '(no answer)' },
          ...approvalsList.map(a => ({ kind: 'approval', ...a, run_id: runId })),
        ];
      });
    } catch (e) {
      setHistory(prev => [
        ...prev.slice(0, -1),
        { kind: 'error', text: e.message || 'Agent failed' },
      ]);
    }
    setBusy(false);
  };

  const approve = async (approval_id) => {
    try {
      const r = await api.post(`/agents/approvals/${approval_id}/approve`, {}, h());
      toast.success(`Approved · ${r.data.status}`);
      setHistory(prev => prev.map(x =>
        x.kind === 'approval' && x.approval_id === approval_id
          ? { ...x, status: 'EXECUTED', exec_result: r.data.result } : x));
    } catch (e) { toast.error('Approval failed'); }
  };

  const reject = async (approval_id) => {
    try {
      await api.post(`/agents/approvals/${approval_id}/reject`,
        { reason: 'User rejected from chat' }, h());
      toast.success('Rejected');
      setHistory(prev => prev.map(x =>
        x.kind === 'approval' && x.approval_id === approval_id
          ? { ...x, status: 'REJECTED' } : x));
    } catch (e) { toast.error('Reject failed'); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-y-0 right-0 z-50 w-[420px] bg-white border-l border-gray-200 shadow-2xl flex flex-col"
      data-testid="agent-chat-panel">
      <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gradient-to-r from-sky-50 to-indigo-50">
        <div className="flex items-center gap-2">
          <Bot className="w-5 h-5 text-sky-600"/>
          <h3 className="text-sm font-semibold text-gray-900">Agentic AI</h3>
          <Badge variant="outline" className="text-[10px] bg-white border-sky-200 text-sky-700">
            Cognara
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Select value={persona} onValueChange={setPersona}>
            <SelectTrigger className="h-7 w-[140px] text-xs border-gray-200"
              data-testid="agent-persona">
              <SelectValue/>
            </SelectTrigger>
            <SelectContent>
              {PERSONAS.map(p => (
                <SelectItem key={p.id} value={p.id} className="text-xs">
                  {p.icon} {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" variant="ghost" onClick={onClose} className="h-7 w-7 p-0"
            data-testid="agent-close">
            <X className="w-4 h-4"/>
          </Button>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2"
        data-testid="agent-thread">
        {history.length === 0 && !busy && (() => {
          const guide = PERSONA_GUIDE[persona] || PERSONA_GUIDE.orchestrator;
          const activePersona = PERSONAS.find(p => p.id === persona);
          return (
            <div className="py-4" data-testid={`agent-empty-state-${persona}`}>
              <div className="flex items-start gap-2 mb-3">
                <div className="w-7 h-7 rounded-md bg-gradient-to-br from-sky-100 to-indigo-100 ring-1 ring-sky-200 flex items-center justify-center shrink-0">
                  <Sparkles className="w-3.5 h-3.5 text-sky-600"/>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] font-semibold text-gray-700 flex items-center gap-1.5">
                    <span>{activePersona?.icon}</span>
                    <span>{activePersona?.label} · Cognara</span>
                  </div>
                  <p className="text-[10.5px] text-gray-500 mt-0.5 leading-relaxed">
                    {guide.blurb}
                  </p>
                </div>
              </div>

              <div className="mb-3">
                <p className="text-[9px] font-semibold uppercase tracking-wider text-gray-400 mb-1">
                  What I can help with
                </p>
                <ul className="space-y-0.5">
                  {guide.cando.map((c, i) => (
                    <li key={i} className="text-[10.5px] text-gray-600 flex gap-1.5">
                      <span className="text-emerald-500 shrink-0">▸</span>
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <p className="text-[9px] font-semibold uppercase tracking-wider text-gray-400 mb-1">
                Try one of these
              </p>
              <div className="space-y-1">
                {guide.prompts.map((p, i) => (
                  <button key={i} onClick={() => setPrompt(p)}
                    className="block w-full text-left px-2 py-1.5 bg-gray-50 rounded-md hover:bg-sky-50 hover:ring-1 hover:ring-sky-200 transition text-[10.5px] text-gray-700 leading-relaxed"
                    data-testid={`agent-suggest-${persona}-${i}`}>
                    {p}
                  </button>
                ))}
              </div>

              <p className="mt-3 text-[9px] text-gray-400 text-center">
                Switch persona above for a different specialist.
              </p>
            </div>
          );
        })()}
        {history.map((m, i) => (
          <MessageRow key={i} m={m} onApprove={approve} onReject={reject}/>
        ))}
        {busy && (
          <div className="flex items-center gap-2 p-2 text-xs text-gray-500"
            data-testid="agent-busy">
            <Loader2 className="w-3 h-3 animate-spin"/>
            Agent is reasoning…
          </div>
        )}
      </div>

      <div className="p-3 border-t border-gray-200 bg-gray-50">
        <Textarea
          value={prompt} onChange={e => setPrompt(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send();
          }}
          placeholder="Ask the agent… (⌘/Ctrl + Enter to send)"
          className="text-xs h-20 resize-none mb-2"
          disabled={busy} data-testid="agent-input"/>
        <div className="flex items-center justify-between">
          <div className="text-[10px] text-gray-400">
            Tools: read-only auto-execute · high-risk needs approval
          </div>
          <Button size="sm" onClick={send} disabled={busy || !prompt.trim()}
            className="h-7 bg-sky-600 hover:bg-sky-500 text-white text-xs"
            data-testid="agent-send">
            <Send className="w-3 h-3 mr-1"/>Send
          </Button>
        </div>
      </div>
    </div>
  );
}

function MessageRow({ m, onApprove, onReject }) {
  const [open, setOpen] = useState(false);
  if (m.kind === 'user') {
    return (
      <div className="flex justify-end" data-testid="msg-user">
        <div className="max-w-[85%] bg-sky-600 text-white text-xs p-2.5 rounded-lg rounded-br-sm">
          {m.text}
        </div>
      </div>
    );
  }
  if (m.kind === 'assistant') {
    return (
      <div className="flex justify-start" data-testid="msg-assistant">
        <div className="max-w-[90%] bg-white border border-gray-200 text-xs p-2.5 rounded-lg rounded-bl-sm whitespace-pre-wrap text-gray-800"
          style={{ wordBreak: 'break-word' }}>
          {m.text}
        </div>
      </div>
    );
  }
  if (m.kind === 'tool') {
    return (
      <div className="text-[10px]" data-testid={`msg-tool-${m.tool}`}>
        <button onClick={() => setOpen(o => !o)}
          className="flex items-center gap-1 text-indigo-600 hover:bg-indigo-50 px-2 py-1 rounded w-full text-left">
          {open ? <ChevronDown className="w-3 h-3"/> : <ChevronRight className="w-3 h-3"/>}
          <Wrench className="w-3 h-3"/>
          <span className="font-mono">{m.tool}</span>
          <span className="text-gray-400 truncate flex-1">{JSON.stringify(m.args)}</span>
        </button>
        {open && (
          <div className="ml-5 mt-1 p-2 bg-gray-50 border border-gray-200 rounded font-mono text-gray-700 max-h-32 overflow-y-auto">
            {m.result || 'no result'}
          </div>
        )}
      </div>
    );
  }
  if (m.kind === 'approval') {
    return (
      <div className="border-2 border-amber-300 bg-amber-50 rounded-lg p-2.5"
        data-testid={`msg-approval-${m.approval_id}`}>
        <div className="flex items-center gap-2 mb-1">
          <AlertTriangle className="w-4 h-4 text-amber-600"/>
          <span className="text-xs font-semibold text-amber-900">Approval required</span>
          <Badge className={`text-[10px] ${m.status === 'EXECUTED' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : m.status === 'REJECTED' ? 'bg-rose-100 text-rose-700 border-rose-200' : 'bg-amber-100 text-amber-700 border-amber-200'} border`}>
            {m.status || 'PENDING'}
          </Badge>
        </div>
        <div className="text-[11px] text-gray-700 mb-2">
          Tool: <code className="text-indigo-700">{m.tool}</code>
        </div>
        <pre className="text-[10px] bg-white p-1.5 rounded border border-amber-200 mb-2 overflow-x-auto">
{JSON.stringify(m.args, null, 2)}
        </pre>
        {!m.status && (
          <div className="flex gap-2">
            <Button size="sm" onClick={() => onApprove(m.approval_id)}
              className="h-6 text-[10px] bg-emerald-600 hover:bg-emerald-500 text-white"
              data-testid={`approve-${m.approval_id}`}>
              <Check className="w-3 h-3 mr-1"/>Approve
            </Button>
            <Button size="sm" variant="outline" onClick={() => onReject(m.approval_id)}
              className="h-6 text-[10px] border-rose-200 text-rose-700"
              data-testid={`reject-${m.approval_id}`}>
              <XIcon className="w-3 h-3 mr-1"/>Reject
            </Button>
          </div>
        )}
      </div>
    );
  }
  if (m.kind === 'error') {
    return (
      <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 p-2 rounded"
        data-testid="msg-error">{m.text}</div>
    );
  }
  if (m.kind === 'streaming') {
    return (
      <div className="flex justify-start" data-testid="msg-streaming">
        <div className="max-w-[90%] bg-sky-50 border border-sky-200 text-xs p-2.5 rounded-lg rounded-bl-sm whitespace-pre-wrap text-gray-800"
          style={{ wordBreak: 'break-word' }}>
          <div className="flex items-center gap-1 text-[10px] text-sky-700 mb-1">
            <Loader2 className="w-3 h-3 animate-spin"/>
            <span className="uppercase tracking-wide">Streaming · {(m.tools || []).length} tool calls</span>
          </div>
          {(m.tools || []).map((t, i) => (
            <div key={i} className="font-mono text-[10px] text-indigo-700 mb-0.5">
              · {t.tool}{t.result ? ' ✓' : ' …'}
            </div>
          ))}
          {m.text && <div className="mt-1 text-gray-800">{m.text}</div>}
        </div>
      </div>
    );
  }
  return null;
}
